import React from "react";
import { Box, Skeleton, useTheme, ThemeAgile } from "@agilekit/ui";
import CalendarClock from "mdi-material-ui/CalendarClock";
import KeyboardOff from "mdi-material-ui/KeyboardOff";
import ReactMarkdown from "react-markdown";
import { useTranslation } from "react-i18next";

import formatDateTime from "../../utils/formatDateTime";
import { ActivityLogFeedItem } from "./ActivityLogFeed.types";
import styles from "./ActivityLogFeed.module.scss";

export interface ActivityLogFeedProps {
	logs: ActivityLogFeedItem[];
	isLoading: boolean;
	renderItem?: (item: ActivityLogFeedItem, index: number) => React.ReactNode;
}

const SKELETON_COUNT = 4;

function ActivityLogFeedSkeleton() {
	const theme: ThemeAgile = useTheme();
	return (
		<>
			{Array.from({ length: SKELETON_COUNT }).map((_, i) => (
				<div key={i} className={styles.skeletonItem}>
					<div className={styles.skeletonHeader}>
						<CalendarClock
							htmlColor={theme.palette.NEUTRAL200}
							style={{ fontSize: 16 }}
						/>
						<Box width={160}>
							<Skeleton variant="text" />
						</Box>
					</div>
					<div className={styles.skeletonBody}>
						<Box width={300}>
							<Skeleton variant="text" />
						</Box>
					</div>
				</div>
			))}
		</>
	);
}

function ActivityLogFeedEmpty() {
	const { t } = useTranslation();
	return (
		<div className={styles.empty}>
			<div className={styles.emptyIcon}>
				<KeyboardOff fontSize="inherit" />
			</div>
			<div>{t("customerView.activity.notFound")}</div>
		</div>
	);
}

function DefaultActivityLogFeedItem({ item }: { item: ActivityLogFeedItem }) {
	const theme: ThemeAgile = useTheme();
	return (
		<div className={styles.item}>
			<div className={styles.itemHeader}>
				<div className={styles.itemIcon}>
					<CalendarClock
						htmlColor={theme.palette.NEUTRAL500}
						style={{ fontSize: 16 }}
					/>
				</div>
				<div className={styles.itemTimestamp}>
					{formatDateTime(item.timeStamp, true)}
				</div>
			</div>
			<div className={styles.itemBody}>
				<div className={styles.bullet} />
				<div className={styles.activityText}>
					<ReactMarkdown linkTarget="_blank">{item.activity}</ReactMarkdown>
				</div>
			</div>
		</div>
	);
}

const ActivityLogFeed: React.FC<ActivityLogFeedProps> = ({
	logs,
	isLoading,
	renderItem,
}) => {
	if (isLoading) {
		return (
			<div className={styles.container}>
				<ActivityLogFeedSkeleton />
			</div>
		);
	}

	if (logs.length === 0) {
		return <ActivityLogFeedEmpty />;
	}

	return (
		<div className={styles.container}>
			{logs.map((item, index) =>
				renderItem ? (
					<React.Fragment key={item.logId ?? index}>
						{renderItem(item, index)}
					</React.Fragment>
				) : (
					<DefaultActivityLogFeedItem key={item.logId ?? index} item={item} />
				)
			)}
		</div>
	);
};

export default ActivityLogFeed;
